﻿import {LocalizationManager} from "./localization";
import {CartDatabase} from "./cart-db";
import {AdminApi, AdminPermissionsMask} from "./admin";

export class ErrorNotification
{
    static #timeoutId = null;

    static Show(message)
    {
        const toast = document.getElementById('error-toast');
        const textElement = document.getElementById('error-toast-text');

        if (!toast || !textElement) return;

        textElement.textContent = message;

        toast.classList.add('show');

        toast.classList.remove('shake');

        void toast.offsetWidth;

        toast.classList.add('shake');

        if (ErrorNotification.#timeoutId)
        {
            clearTimeout(ErrorNotification.#timeoutId);
        }

        ErrorNotification.#timeoutId = setTimeout(() => {
            toast.classList.remove('show');
        }, 3000);
    }
}

export class Params
{
    static GetMaxProductAmountInCart()
    {
        return 10; // Sync with backend variable
    }
}

export class Task
{
    static Delay(milliseconds)
    {
        return new Promise(resolve => setTimeout(resolve, milliseconds));
    }
}

export class CookieHelper
{
    static GetCookie(name)
    {
        const regex = new RegExp(`(?:(?:^|;\\s*)${name}=([^;]*))`);
        const match = document.cookie.match(regex);
        return match ? decodeURIComponent(match[1]) : null;
    }

    static SetCookie(name, value)
    {
        document.cookie = `${name}=${value}; path=/; SameSite=Lax`;
    }

    static SetExpiredCookie(name)
    {
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }
}

export class EnumParser
{
    static Parse(data, enumSchema)
    {
        if (data === null || data === undefined)
        {
            return 0;
        }

        if (typeof data === 'number')
        {
            return data;
        }

        const rawString = String(data);

        return rawString
            .split(',')
            .map(part => part.trim().toLowerCase())
            .filter(part => part !== "")
            .reduce((mask, key) => {
                const value = enumSchema[key];
                return value !== undefined ? mask | value : mask;
            }, 0);
    }

    static HasFlag(mask, flag)
    {
        if (!mask || !flag) return false;
        return (mask & flag) === flag;
    }
}

export class ApiHelper
{
    static async FetchJsonAndProcessErrorsAsync(url, init)
    {
        try
        {
            const response = await fetch(url, init);

            if (!response.ok)
            {
                if (response.headers.get("Content-Type")?.includes("application/json"))
                {
                    let jsonData = await response.json();

                    if (!jsonData.error)
                    {
                        jsonData.error = "unknown_error";
                    }

                    return jsonData;
                }

                if (response.status === 429)
                {
                    return {error: "too_many_requests"};
                }

                if (response.status >= 500)
                {
                    return {error: "server_error"};
                }

                return {error: "request_error"};
            }

            if (response.headers.get("Content-Type")?.includes("application/json"))
            {
                return await response.json();
            }

            return {error: "invalid_response"};
        }
        catch
        {
            return {error: "network_error"};
        }
    }
}

export class Base64Helper
{
    static ConvertBytesToBase64String(bytes)
    {
        if (!(bytes instanceof Uint8Array))
        {
            throw new TypeError("Invalid bytes type!");
        }

        const binaryString = String.fromCharCode(...bytes);

        return btoa(binaryString);
    }

    static ConvertBase64StringToBytes(base64String)
    {
        if (!(typeof (base64String) === 'string' || base64String instanceof String))
        {
            throw new TypeError("Invalid base64String type!");
        }

        const binaryString = atob(base64String);
        const bytes = new Uint8Array(binaryString.length);

        for (let i = 0; i < binaryString.length; i++)
        {
            bytes[i] = binaryString.charCodeAt(i);
        }

        return bytes;
    }

    static ConvertBase64StringToString(base64String)
    {
        const bytes = Base64Helper.ConvertBase64StringToBytes(base64String);

        return new TextDecoder().decode(bytes);
    }
}

export class DataBus
{
    static #channels = {};

    static Subscribe(channelName, listener)
    {
        if (!listener) return;

        let channel = DataBus.#channels[channelName];

        if (!channel)
        {
            channel = {
                listeners: [],
                data: null
            };

            DataBus.#channels[channelName] = channel;
        }

        channel.listeners.push(listener);

        if (channel.data)
        {
            listener(channel.data);
        }
    }

    static Unsubscribe(channelName, listener)
    {
        if (!listener) return;

        const channel = DataBus.#channels[channelName];
        if (!channel) return;

        const index = channel.listeners.indexOf(listener);

        if (index !== -1)
        {
            channel.listeners.splice(index, 1);
        }

        if (channel.listeners.length === 0 && channel.data === null)
        {
            delete DataBus.#channels[channelName];
        }
    }

    static WaitForDataAsync(channelName)
    {
        return new Promise((resolve) => {

            const tempListener = (data) => {

                DataBus.Unsubscribe(channelName, tempListener);

                resolve(data);
            };

            DataBus.Subscribe(channelName, tempListener);
        });
    }

    static SetData(channelName, data)
    {
        let channel = DataBus.#channels[channelName];

        if (channel)
        {
            channel.data = data;
        }
        else
        {
            channel = {
                listeners: [],
                data: data
            };

            DataBus.#channels[channelName] = channel;
        }

        const listeners = channel.listeners;

        [...listeners].forEach(listener => listener(data));
    }
}

export class AnimationController
{
    static #observer;

    static Create()
    {
        AnimationController.#observer = new IntersectionObserver((entries) => AnimationController.#OnIntersection(entries), {threshold: 0.1});
        AnimationController.#ObserveAll();
    }

    static Observe(element)
    {
        AnimationController.#observer.observe(element);
    }

    static #ObserveAll()
    {
        for (const el of document.querySelectorAll('[data-animate]'))
        {
            AnimationController.Observe(el);
        }
    }

    static #OnIntersection(entries)
    {
        for (const entry of entries)
        {
            if (entry.isIntersecting)
            {
                const animation = entry.target.dataset.animate;
                if (animation)
                {
                    entry.target.classList.add('animate__animated', animation);
                }
                AnimationController.#observer.unobserve(entry.target);
            }
        }
    }
}

export class HeaderTopMenuController
{
    static HighlightItem(menuId)
    {
        const allLinks = document.querySelectorAll('.header__nav-list .header__nav-link');

        for (const link of allLinks)
        {
            link.classList.remove('header__nav-link--active');
        }

        if (menuId)
        {
            const targetLink = document.getElementById(menuId);
            if (targetLink)
            {
                targetLink.classList.add('header__nav-link--active');
            }
        }
    }
}

class BaseApi
{
    static async SetCurrentUserLanguage(langId)
    {
        const params = new URLSearchParams({
            lang_id: langId
        });

        const url = `/set_current_user_language.php?${params.toString()}`;

        return await ApiHelper.FetchJsonAndProcessErrorsAsync(url, {
            method: 'GET'
        });
    }

    static async GetCurrentUserProfileInfo()
    {
        const url = '/get_current_user_profile_info.php';

        return await ApiHelper.FetchJsonAndProcessErrorsAsync(url, {
            method: 'GET'
        });
    }
}

class LanguageButtonController
{
    #buttons;

    constructor()
    {
        this.#buttons = document.querySelectorAll('.lng-switcher__btn');

        if (this.#buttons.length > 0)
        {
            this.#SetActiveButtonBasedOnHtmlLang();
            this.#Initialize();
        }
    }

    #SetActiveButtonBasedOnHtmlLang()
    {
        const currentLang = document.documentElement.lang;

        if (!currentLang) return;

        this.#buttons.forEach(btn => {

            btn.style.transition = 'none';

            if (btn.dataset.lang === currentLang)
            {
                btn.classList.add('lng-switcher__btn--active');
            }
            else
            {
                btn.classList.remove('lng-switcher__btn--active');
            }

            requestAnimationFrame(() => {
                btn.style.transition = '';
            });
        });
    }

    #Initialize()
    {
        this.#buttons.forEach(btn => {
            btn.addEventListener('click', async (e) => {
                e.preventDefault();

                if (btn.classList.contains('lng-switcher__btn--active')) return;

                const lang = btn.dataset.lang;

                await this.#ChangeLanguageAsync(lang);
            });
        });
    }

    async #ChangeLanguageAsync(lang)
    {
        let langId;

        switch (lang)
        {
            case "en":
                langId = 1;
                break;
            case "ru":
                langId = 2;
                break;
            case "zh":
                langId = 3;
                break;
            default:
                ErrorNotification.Show("Unknown language: " + lang);
                return;
        }

        const result = await BaseApi.SetCurrentUserLanguage(langId);

        if (result.error)
        {
            ErrorNotification.Show("Language switch failed! Error code: " + result.error);
        }
        else
        {
            window.location.reload();
        }
    }
}

class HeaderMenuController
{
    #mainBtn;
    #menu;

    constructor()
    {
        this.#mainBtn = document.querySelector('.main-menu__head');
        this.#menu = document.querySelector('.main-menu');

        if (this.#mainBtn && this.#menu)
        {
            this.#Initialize();

            this.#InitAsync();
        }
    }

    async #InitAsync()
    {
        await this.#FetchAndStoreUserInfoAsync();

        await this.#UpdateVisibilityAsync();
        await this.#UpdateAdminUiVisibilityAsync();
    }

    async #FetchAndStoreUserInfoAsync()
    {
        let userInfo;

        let delay = 500;

        while (true)
        {
            userInfo = await BaseApi.GetCurrentUserProfileInfo();

            if (userInfo.error)
            {
                if (userInfo.error === "not_authorized")
                {
                    userInfo = {guest: true};

                    break;
                }
                else
                {
                    await Task.Delay(delay);

                    delay = delay * 2;
                    if (delay > 4000) delay = 4000;
                }
            }
            else
            {
                break;
            }
        }

        DataBus.SetData("current_user_info", userInfo);
    }

    async #UpdateVisibilityAsync()
    {
        const userInfo = await DataBus.WaitForDataAsync("current_user_info");

        const authButtonEl = document.getElementById('profile-auth-button');
        const registerButtonEl = document.getElementById('profile-register-button');
        const ordersButtonEl = document.getElementById('profile-orders-button');
        const logoutButtonEl = document.getElementById('profile-logout-button');

        const usernameEl = document.getElementById('profile-username');
        const avatarEl = document.querySelector('.main-menu__head-user-avatar');

        if (userInfo.user_id)
        {
            if (authButtonEl) authButtonEl.style.display = 'none';
            if (registerButtonEl) registerButtonEl.style.display = 'none';
            if (ordersButtonEl) ordersButtonEl.style.display = '';
            if (logoutButtonEl) logoutButtonEl.style.display = '';

            if (usernameEl) usernameEl.textContent = userInfo.username;

            if (avatarEl)
            {
                if (userInfo.avatar)
                {
                    avatarEl.style.visibility = 'visible';
                    avatarEl.style.width = '';
                    avatarEl.style.marginLeft = '';
                    avatarEl.style.marginRight = '';

                    avatarEl.style.backgroundImage = `url('${userInfo.avatar}')`;
                    avatarEl.style.backgroundSize = 'cover';
                    avatarEl.style.backgroundPosition = 'center';
                }
                else
                {
                    avatarEl.style.visibility = 'hidden';
                    avatarEl.style.width = '0px';
                    avatarEl.style.marginLeft = '0px';
                    avatarEl.style.marginRight = '0px';
                }
            }
        }
        else
        {
            if (authButtonEl)
            {
                authButtonEl.addEventListener('click', () => {
                    window.location.href = '/forum/login';
                });
            }

            if (authButtonEl) authButtonEl.style.display = '';
            if (registerButtonEl) registerButtonEl.style.display = '';
            if (ordersButtonEl) ordersButtonEl.style.display = 'none';
            if (logoutButtonEl) logoutButtonEl.style.display = 'none';

            if (usernameEl && userInfo.guest) usernameEl.textContent = LocalizationManager.GetText("profile-guest-name");

            if (avatarEl)
            {
                avatarEl.style.visibility = 'hidden';
                avatarEl.style.width = '0px';
                avatarEl.style.marginLeft = '0px';
                avatarEl.style.marginRight = '0px';
            }
        }
    }

    async #UpdateAdminUiVisibilityAsync()
    {
        const permissions = await AdminApi.GetPermissionsAsync();
        const isAdmin = EnumParser.HasFlag(permissions, AdminPermissionsMask.admin);

        if (!isAdmin)
        {
            return;
        }

        const adminPanelButtonEl = document.getElementById('profile-admin-panel-button');

        if (adminPanelButtonEl) adminPanelButtonEl.style.display = 'block';
    }

    #Initialize()
    {
        this.#mainBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.#menu.classList.toggle('active');
        });

        document.addEventListener('click', (e) => {
            if (!this.#menu.contains(e.target) && !this.#mainBtn.contains(e.target))
            {
                this.#menu.classList.remove('active');
            }
        });
    }
}

class LauncherDownloadButtonsController
{
    constructor()
    {
        const launcherDownloadMainButton = document.getElementById("launcher-download-main-button");

        if (launcherDownloadMainButton)
        {
            launcherDownloadMainButton.addEventListener('click', async (event) => {
                event.stopPropagation();

                await this.#DownloadLauncher();
            });
        }

        const launcherDownloadProfileButton = document.getElementById("launcher-download-profile-button");

        if (launcherDownloadProfileButton)
        {
            launcherDownloadProfileButton.addEventListener('click', async (event) => {
                event.stopPropagation();

                await this.#DownloadLauncher();
            });
        }
    }

    async #DownloadLauncher()
    {
        let uri;

        try
        {
            const response = await fetch("/get_content_region", {signal: AbortSignal.timeout(2000)});

            if (!response.ok)
            {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const textData = await response.text();

            if (textData === "RU")
            {
                uri = "https://cdny.bannerlord-online.com/launcher/BannerlordOnlineSetup.exe";
            }
            else if (textData === "CN")
            {
                uri = "https://bannerlord-online.oss-cn-hangzhou.aliyuncs.com/launcher/BannerlordOnlineSetup.exe";
            }
            else
            {
                uri = "https://cdn.bannerlord-online.com/launcher/BannerlordOnlineSetup.exe";
            }
        }
        catch (error)
        {
            console.error("Failed detecting server region. Used default downloading link.", error);

            uri = "https://cdn.bannerlord-online.com/launcher/BannerlordOnlineSetup.exe";
        }

        uri += "?timestamp=" + Date.now();

        window.location.href = uri;
    }
}

class CartButtonController
{
    #countElement;

    #db;

    constructor()
    {
        this.#countElement = document.querySelector('.cart-btn__num');

        if (this.#countElement)
        {
            this.#InitAsync();
        }
    }

    async #InitAsync()
    {
        this.#db = await CartDatabase.OpenDatabaseAsync();

        const channel = new BroadcastChannel("cart_change_channel");

        channel.onmessage = () => {
            this.#RefreshButtonAsync();
        };

        this.#RefreshButtonAsync();
    }

    async #RefreshButtonAsync()
    {
        if (this.#db.IsClosed()) return;

        let totalSum;

        try
        {
            totalSum = await this.#db.GetProductsAmountSumAsync();
        }
        catch (e)
        {
            console.error("Cart button update failed", e);

            totalSum = 0;
        }

        if (totalSum > 0)
        {
            this.#countElement.textContent = totalSum.toString();
            this.#countElement.style.display = "";
        }
        else
        {
            this.#countElement.style.display = "none";
        }
    }
}

export class PopupController
{
    static #actionHandler = null;
    static #closeHandler = null;
    static #pasteHandler = null;
    static #currentImageFiles = [];
    static #maxImages = 1;

    static #actionInProgress = false;
    static #progressTextRedefined = false;

    static #draggedItemIndex = null;

    static SetProgressMessage(message)
    {
        const continueTextEl = document.getElementById("popup-continue-text");
        if (continueTextEl)
        {
            continueTextEl.textContent = message;
        }

        PopupController.#progressTextRedefined = true;
    }

    static #UpdateGallery()
    {
        const gallery = document.getElementById("popup-image-gallery");
        const placeholder = document.getElementById("popup-image-placeholder");

        if (!gallery || !placeholder) return;

        gallery.innerHTML = "";

        if (this.#currentImageFiles.length > 0)
        {
            gallery.style.display = "flex";
            if (this.#currentImageFiles.length >= this.#maxImages)
            {
                placeholder.style.display = "none";
            }
            else
            {
                placeholder.style.display = "block";
                placeholder.textContent = `Added: ${this.#currentImageFiles.length}/${this.#maxImages} images`;
            }
        }
        else
        {
            gallery.style.display = "none";
            placeholder.style.display = "block";
            placeholder.textContent = "Click to select images or paste image with Ctrl+V";
        }

        this.#currentImageFiles.forEach((file, index) => {
            const imgContainer = document.createElement('div');
            imgContainer.style.position = 'relative';
            imgContainer.style.transition = 'transform 0.2s, opacity 0.2s';

            imgContainer.draggable = true;

            const img = document.createElement('img');
            img.src = URL.createObjectURL(file);
            img.style.width = "80px";
            img.style.height = "80px";
            img.style.objectFit = "cover";
            img.style.borderRadius = "4px";
            img.style.border = "1px solid var(--bg-2, #544a3e)";

            img.style.pointerEvents = "none";

            const delBtn = document.createElement('div');
            delBtn.innerHTML = "&times;";
            delBtn.dataset.action = "delete";
            delBtn.style.position = "absolute";
            delBtn.style.top = "-5px";
            delBtn.style.right = "-5px";
            delBtn.style.background = "#e74c3c";
            delBtn.style.color = "#fff";
            delBtn.style.borderRadius = "50%";
            delBtn.style.width = "20px";
            delBtn.style.height = "20px";
            delBtn.style.display = "flex";
            delBtn.style.alignItems = "center";
            delBtn.style.justifyContent = "center";
            delBtn.style.fontSize = "16px";
            delBtn.style.cursor = "pointer";
            delBtn.style.zIndex = "2";

            delBtn.onclick = (event) => {
                event.stopPropagation();

                if (!PopupController.#actionInProgress)
                {
                    this.#currentImageFiles.splice(index, 1);
                    this.#UpdateGallery();
                }
            };

            imgContainer.addEventListener('dragstart', (e) => {

                if (PopupController.#actionInProgress)
                {
                    e.preventDefault();
                }
                else
                {
                    this.#draggedItemIndex = index;
                    e.dataTransfer.effectAllowed = 'move';
                    setTimeout(() => imgContainer.style.opacity = '0.5', 0);
                }
            });

            imgContainer.addEventListener('dragend', () => {
                imgContainer.style.opacity = '1';
                this.#draggedItemIndex = null;
            });

            imgContainer.addEventListener('dragover', (e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';

                if (this.#draggedItemIndex !== index)
                {
                    imgContainer.style.transform = 'scale(1.05) translateY(-2px)';
                }
            });

            imgContainer.addEventListener('dragleave', () => {

                imgContainer.style.transform = 'none';
            });

            imgContainer.addEventListener('drop', (e) => {
                e.preventDefault();
                imgContainer.style.transform = 'none';

                if (!PopupController.#actionInProgress)
                {
                    const draggedIdx = this.#draggedItemIndex;
                    const droppedIdx = index;

                    if (draggedIdx !== null && draggedIdx !== droppedIdx)
                    {
                        const draggedFile = this.#currentImageFiles.splice(draggedIdx, 1)[0];
                        this.#currentImageFiles.splice(droppedIdx, 0, draggedFile);
                        this.#UpdateGallery();
                    }
                }
            });

            imgContainer.appendChild(img);
            imgContainer.appendChild(delBtn);
            gallery.appendChild(imgContainer);
        });
    }

    static OpenPopup({
                         caption,
                         message,
                         actionText,
                         closeText,
                         onAction,
                         onClose,
                         showInput,
                         initialInputText,
                         isMultiline,
                         inputPlaceholderText,
                         showImagePicker,
                         continueText,
                         maxImages = 1,
                         tags,
                         multipleTags
                     })
    {
        PopupController.#progressTextRedefined = false;

        PopupController.#maxImages = maxImages;

        const overlay = document.getElementById("popup-overlay");
        const popupElement = overlay.querySelector(".popup");

        const captionTextElement = document.getElementById("popup-caption-text");
        const bodyTextElement = document.getElementById("popup-body-text");
        const actionBtn = document.getElementById("popup-action-button");
        const closeBtn = document.getElementById("popup-close-button");

        const inputWrapper = document.getElementById("popup-input-wrapper");
        const inputField = document.getElementById("popup-input-field");
        const textareaField = document.getElementById("popup-textarea-field");

        const imageWrapper = document.getElementById("popup-image-wrapper");
        const imageInput = document.getElementById("popup-image-input");
        const imageGallery = document.getElementById("popup-image-gallery");

        const tagsWrapper = document.getElementById("popup-tags-wrapper");

        const buttonsWrapper = document.getElementById("popup-buttons-wrapper");
        const continueTextEl = document.getElementById("popup-continue-text");

        const allowedTypes = ['image/bmp', 'image/jpg', 'image/jpeg', 'image/png', 'image/webp'];

        imageInput.setAttribute("accept", allowedTypes.join(", "));

        captionTextElement.innerHTML = caption || "";
        bodyTextElement.innerHTML = message || "";

        PopupController.#actionInProgress = false;

        if (showInput && inputWrapper)
        {
            inputWrapper.style.display = "block";

            if (isMultiline && textareaField)
            {
                if (inputField) inputField.style.display = "none";
                textareaField.style.display = "block";
                textareaField.value = initialInputText ?? "";
                textareaField.placeholder = inputPlaceholderText || "";
            }
            else if (inputField)
            {
                if (textareaField) textareaField.style.display = "none";
                inputField.style.display = "block";
                inputField.value = initialInputText ?? "";
                inputField.placeholder = inputPlaceholderText || "";
            }

            popupElement.classList.add("popup--multiline");
        }
        else if (inputWrapper)
        {
            inputWrapper.style.display = "none";
            popupElement.classList.remove("popup--multiline");
        }

        if (tagsWrapper)
        {
            tagsWrapper.innerHTML = "";

            if (tags && tags.length > 0)
            {
                tagsWrapper.style.display = "flex";

                popupElement.classList.add("popup--multiline");

                tags.forEach((tag) => {
                    const label = document.createElement("label");
                    label.className = "popup__tag-label";

                    const input = document.createElement("input");
                    input.type = multipleTags ? "checkbox" : "radio";
                    input.name = "popup-tag-selection";
                    input.value = tag.value || tag;
                    input.className = "popup__tag-input";

                    if (multipleTags && tag.selected)
                    {
                        input.checked = true;
                    }

                    const span = document.createElement("span");
                    span.className = "popup__tag-text text-main";
                    span.textContent = tag.label || tag;

                    label.appendChild(input);
                    label.appendChild(span);
                    tagsWrapper.appendChild(label);
                });
            }
            else
            {
                tagsWrapper.style.display = "none";
            }
        }

        if (showImagePicker && imageWrapper)
        {
            imageWrapper.style.display = "block";
            popupElement.classList.add("popup--multiline");

            PopupController.#currentImageFiles = [];

            if (imageGallery)
            {
                PopupController.#UpdateGallery();
            }

            const addImage = (file, skipUpdate = false) => {

                if (PopupController.#actionInProgress) return false;

                if (!file) return false;

                if (!allowedTypes.includes(file.type))
                {
                    if (typeof ErrorNotification !== 'undefined')
                    {
                        ErrorNotification.Show("Invalid image format!");
                    }
                    return false;
                }

                if (PopupController.#currentImageFiles.length >= PopupController.#maxImages)
                {
                    if (typeof ErrorNotification !== 'undefined')
                    {
                        ErrorNotification.Show(`You can add up to ${PopupController.#maxImages} images!`);
                    }
                    return false;
                }

                PopupController.#currentImageFiles.push(file);

                if (!skipUpdate)
                {
                    PopupController.#UpdateGallery();
                }
                return true;
            };

            imageWrapper.onclick = (e) => {

                if (PopupController.#actionInProgress) return;

                if (e.target.dataset.action === "delete") return;

                if (PopupController.#currentImageFiles.length < PopupController.#maxImages)
                {
                    imageInput.click();
                }
            };

            imageInput.onchange = (e) => {

                if (PopupController.#actionInProgress) return;

                if (e.target.files && e.target.files.length > 0)
                {
                    let filesAdded = false;
                    Array.from(e.target.files).forEach(file => {
                        if (addImage(file, true))
                        {
                            filesAdded = true;
                        }
                    });

                    if (filesAdded)
                    {
                        PopupController.#UpdateGallery();
                    }
                }
                imageInput.value = "";
            };

            if (PopupController.#pasteHandler)
            {
                document.removeEventListener('paste', PopupController.#pasteHandler);
            }

            PopupController.#pasteHandler = (e) => {

                if (PopupController.#actionInProgress) return;

                if (imageWrapper.style.display !== "none" && e.clipboardData)
                {
                    const items = e.clipboardData.items;
                    let filesAdded = false;

                    for (let i = 0; i < items.length; i++)
                    {
                        if (items[i].type.indexOf("image") !== -1)
                        {
                            const blob = items[i].getAsFile();
                            if (addImage(blob, true))
                            {
                                filesAdded = true;
                            }
                        }
                    }

                    if (filesAdded)
                    {
                        PopupController.#UpdateGallery();
                    }
                }
            };
            document.addEventListener('paste', PopupController.#pasteHandler);
        }
        else if (imageWrapper)
        {
            imageWrapper.style.display = "none";
        }

        if (buttonsWrapper) buttonsWrapper.style.display = "flex";
        if (continueTextEl) continueTextEl.style.display = "none";

        if (actionText)
        {
            const actionTextEl = actionBtn.querySelector(".second-btn__text");
            if (actionTextEl) actionTextEl.textContent = actionText;
            actionBtn.style.display = "inline-flex";
        }
        else
        {
            actionBtn.style.display = "none";
        }

        if (closeText)
        {
            const closeTextEl = closeBtn.querySelector(".second-btn__text");
            if (closeTextEl) closeTextEl.textContent = closeText;
            closeBtn.style.display = "inline-flex";
        }
        else
        {
            closeBtn.style.display = "none";
        }

        if (PopupController.#actionHandler)
        {
            actionBtn.removeEventListener("click", PopupController.#actionHandler);
        }
        if (PopupController.#closeHandler)
        {
            closeBtn.removeEventListener("click", PopupController.#closeHandler);
        }

        PopupController.#actionHandler = async () => {

            if (PopupController.#actionInProgress) return;

            let inputValue = null;

            const hasTags = tags && tags.length > 0;

            if (showInput)
            {
                const activeField = isMultiline ? textareaField : inputField;
                if (activeField)
                {
                    inputValue = activeField.value;
                }

                if (!inputValue && !showImagePicker && !hasTags) return;
            }

            let selectedTags = [];
            if (hasTags && tagsWrapper)
            {
                selectedTags = Array.from(tagsWrapper.querySelectorAll('input[name="popup-tag-selection"]:checked'))
                    .map(input => input.value);
            }

            let resultPayload;

            if (!showImagePicker && !hasTags)
            {
                resultPayload = inputValue;
            }
            else
            {
                resultPayload = {};
                if (showInput || inputValue) resultPayload.text = inputValue;
                if (showImagePicker) resultPayload.images = [...PopupController.#currentImageFiles];
                if (hasTags) resultPayload.tags = selectedTags;
            }

            try
            {
                if (onAction)
                {
                    const result = onAction(resultPayload);

                    if (result instanceof Promise)
                    {
                        buttonsWrapper.style.display = "none";

                        if (continueTextEl)
                        {
                            continueTextEl.style.display = "block";

                            if (!PopupController.#progressTextRedefined)
                            {
                                continueTextEl.textContent = continueText ?? "";
                            }
                        }

                        PopupController.#actionInProgress = true;

                        await result;
                    }
                }
            }
            catch (error)
            {
                console.error("Popup action execution failed:", error);
            }
            finally
            {
                PopupController.#actionInProgress = false;
                PopupController.ClosePopup();
            }
        };

        PopupController.#closeHandler = () => {

            if (PopupController.#actionInProgress) return;

            PopupController.ClosePopup();

            if (onClose)
            {
                onClose();
            }
        };

        actionBtn.addEventListener("click", PopupController.#actionHandler);
        closeBtn.addEventListener("click", PopupController.#closeHandler);

        let mouseDownTarget = null;

        overlay.onmousedown = (e) => {
            mouseDownTarget = e.target;
        };

        overlay.onmouseup = (e) => {
            if (e.target === overlay && mouseDownTarget === overlay)
            {
                if (!PopupController.#actionInProgress)
                {
                    PopupController.ClosePopup();

                    if (onClose)
                    {
                        onClose();
                    }
                }
            }
            mouseDownTarget = null;
        };

        overlay.classList.add("active");
    }

    static ClosePopup()
    {
        const overlay = document.getElementById("popup-overlay");
        if (overlay) overlay.classList.remove("active");

        if (PopupController.#pasteHandler)
        {
            document.removeEventListener('paste', PopupController.#pasteHandler);
            PopupController.#pasteHandler = null;
        }

        PopupController.#currentImageFiles = [];

        const imageGallery = document.getElementById("popup-image-gallery");
        const imagePlaceholder = document.getElementById("popup-image-placeholder");
        const imageInput = document.getElementById("popup-image-input");
        const buttonsWrapper = document.getElementById("popup-buttons-wrapper");
        const continueTextEl = document.getElementById("popup-continue-text");

        const tagsWrapper = document.getElementById("popup-tags-wrapper");

        if (imageGallery)
        {
            imageGallery.innerHTML = "";
            imageGallery.style.display = "none";

            if (imagePlaceholder)
            {
                imagePlaceholder.style.display = "block";
                imagePlaceholder.textContent = "Click to select images or paste image with Ctrl+V";
            }

            if (imageInput) imageInput.value = "";
        }

        if (tagsWrapper)
        {
            tagsWrapper.innerHTML = "";
            tagsWrapper.style.display = "none";
        }

        if (buttonsWrapper) buttonsWrapper.style.display = "flex";
        if (continueTextEl) continueTextEl.style.display = "none";
    }
}

class BaseController
{
    static Init()
    {
        AnimationController.Create();

        new LanguageButtonController();

        new HeaderMenuController();

        new LauncherDownloadButtonsController();

        new CartButtonController();
    }
}

document.addEventListener('DOMContentLoaded', BaseController.Init);